function whenSplideReady(callback, attempt = 0) {
  if (typeof window.Splide === 'function') return callback()
  if (attempt > 50) return
  requestAnimationFrame(() => whenSplideReady(callback, attempt + 1))
}

function setCarouselCarets(root) {
  const prevTemplate = root.querySelector('[data-carousel-caret-prev]')
  const nextTemplate = root.querySelector('[data-carousel-caret-next]')
  const prev = root.querySelector('[data-main-carousel] .splide__arrow--prev')
  const next = root.querySelector('[data-main-carousel] .splide__arrow--next')

  if (prev && prevTemplate) prev.innerHTML = prevTemplate.innerHTML
  if (next && nextTemplate) next.innerHTML = nextTemplate.innerHTML
}

function getCarouselSlideKey(slide) {
  return slide.dataset.heroOfferSlideIndex || slide.dataset.carouselSlideId || ''
}

function sortCarouselSlidesByBlockOrder(slides) {
  return [...slides].sort((a, b) => Number(getCarouselSlideKey(a) || 0) - Number(getCarouselSlideKey(b) || 0))
}

function dedupeCarouselSlides(slides) {
  const seen = new Set()

  return slides.filter((slide) => {
    const key = getCarouselSlideKey(slide)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function rememberOriginalCarouselOrder(root) {
  if (root._heroOfferOriginalMainSlides?.length) return

  const mainList = root.querySelector('[data-main-carousel] .splide__list')
  const thumbList = root.querySelector('[data-thumb-carousel] .splide__list')

  root._heroOfferOriginalMainSlides = mainList
    ? sortCarouselSlidesByBlockOrder(
        dedupeCarouselSlides([...mainList.querySelectorAll('[data-hero-offer-carousel-slide]')])
      )
    : []
  root._heroOfferOriginalThumbSlides = thumbList
    ? sortCarouselSlidesByBlockOrder(
        dedupeCarouselSlides([...thumbList.querySelectorAll('[data-hero-offer-carousel-slide]')])
      )
    : []
}

function slideMatchesOffer(slide, offerPosition) {
  const scopedOffer = slide.dataset.offerPosition || ''
  return !scopedOffer || scopedOffer === offerPosition
}

function getVisibleOfferSlides(originalSlides, offerPosition) {
  let visibleSlides = originalSlides.filter((slide) => slideMatchesOffer(slide, offerPosition))

  if (visibleSlides.length === 0) {
    visibleSlides = originalSlides.filter((slide) => !slide.dataset.offerPosition)
  }

  return sortCarouselSlidesByBlockOrder(visibleSlides)
}

function reorderHeroOfferThumbSlides(root, orderedMainSlides) {
  const thumbCarousel = root.querySelector('[data-thumb-carousel]')
  const thumbList = thumbCarousel?.querySelector('.splide__list')
  if (!thumbList || !root._heroOfferOriginalThumbSlides?.length) return 0

  const thumbByIndex = new Map(root._heroOfferOriginalThumbSlides.map((slide) => [getCarouselSlideKey(slide), slide]))

  const orderedThumbSlides = orderedMainSlides
    .map((mainSlide) => thumbByIndex.get(getCarouselSlideKey(mainSlide)))
    .filter(Boolean)

  if (orderedThumbSlides.length > 0) {
    thumbList.replaceChildren(...orderedThumbSlides)
  }

  if (thumbCarousel) thumbCarousel.hidden = orderedThumbSlides.length <= 1

  return orderedThumbSlides.length
}

function syncHeroOfferCarousel(root, offerPosition, { attempt = 0 } = {}) {
  const splide = root._heroOfferMediaSplide
  if (!splide) {
    if (attempt < 60) {
      requestAnimationFrame(() => syncHeroOfferCarousel(root, offerPosition, { attempt: attempt + 1 }))
    }
    return
  }

  if (!root._heroOfferOriginalMainSlides?.length) return

  const orderedMainSlides = getVisibleOfferSlides(root._heroOfferOriginalMainSlides, offerPosition)
  const mainList = root.querySelector('[data-main-carousel] .splide__list')

  if (!mainList || orderedMainSlides.length === 0) return

  mainList.replaceChildren(...orderedMainSlides)
  reorderHeroOfferThumbSlides(root, orderedMainSlides)

  try {
    splide.refresh()
    root._heroOfferThumbSplide?.refresh?.()
  } catch (_) {}

  try {
    splide.go(0)
  } catch (_) {}
}

function syncHeroOfferCarousels(sectionRoot, offerPosition) {
  if (!sectionRoot || !offerPosition) return

  sectionRoot.querySelectorAll('[data-carousel-offer-images]').forEach((root) => {
    syncHeroOfferCarousel(root, offerPosition)
  })
}

function initMediaCarouselRoot(root) {
  const SplideCtor = window.Splide
  if (!root || root.dataset.initialized === 'true' || typeof SplideCtor !== 'function') return
  if (root.dataset.mediaInitPending === 'true') return

  const mainEl = root.querySelector('[data-main-carousel]')
  if (!mainEl) return

  root.dataset.mediaInitPending = 'true'

  try {
    const thumbsEl = root.querySelector('[data-thumb-carousel]')
    const enableLoop = root.dataset.enableLoop !== 'false'
    const showArrows = root.dataset.showArrows !== 'false'

    if (root.dataset.carouselOfferImages === 'true') {
      rememberOriginalCarouselOrder(root)
    }

    const main = new SplideCtor(mainEl, {
      type: enableLoop ? 'loop' : 'slide',
      rewind: !enableLoop,
      arrows: showArrows,
      pagination: false,
      gap: 0,
    })

    if (thumbsEl) {
      const thumbs = new SplideCtor(thumbsEl, {
        fixedWidth: 72,
        fixedHeight: 72,
        gap: '8px',
        rewind: true,
        pagination: false,
        isNavigation: true,
        arrows: false,
        cover: true,
        breakpoints: {
          640: {
            fixedWidth: 56,
            fixedHeight: 56,
          },
        },
      })

      main.sync(thumbs)
      main.mount()
      thumbs.mount()
      root._heroOfferThumbSplide = thumbs
    } else {
      main.mount()
    }

    setCarouselCarets(root)
    root.dataset.initialized = 'true'
    root._heroOfferMediaSplide = main
  } finally {
    delete root.dataset.mediaInitPending
  }
}

function initMediaCarousels(scope = document) {
  scope.querySelectorAll('.section-hero-offer [data-main-product-carousel]').forEach(initMediaCarouselRoot)
}

class HeroOffer extends HTMLElement {
  connectedCallback() {
    this._lastCarouselOfferPosition = null
    this.addEventListener('change', this.onChange)
    this.getSectionRoot()?.addEventListener('change', this.onSectionChange)
    document.addEventListener('product-form:before-submit', this.onBeforeSubmit)
    window.requestAnimationFrame(() => this.sync())
    window.setTimeout(() => this.sync(), 0)

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.onDomReady, { once: true })
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange)
    this.getSectionRoot()?.removeEventListener('change', this.onSectionChange)
    document.removeEventListener('product-form:before-submit', this.onBeforeSubmit)
    document.removeEventListener('DOMContentLoaded', this.onDomReady)

    const form = this.getForm()
    if (!form) return

    this.removeOfferInputs(form)
    this.setBaseInputsDisabled(form, false)
  }

  onChange = () => {
    this.sync()
  }

  onSectionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return
    if (!this.getSectionRoot()?.contains(event.target)) return
    if (!event.target.matches('[data-hero-offer-switch], [data-hero-offer-option-input]')) return
    this.sync()
  }

  onDomReady = () => {
    this.sync()
  }

  onBeforeSubmit = (event) => {
    const form = this.getForm()
    const submitForm = event.target instanceof HTMLFormElement ? event.target : form

    if (!form || submitForm !== form) return
    this.sync()
  }

  getForm() {
    if (!this.dataset.formId) return null
    return document.getElementById(this.dataset.formId)
  }

  getSectionRoot() {
    return document.querySelector(`[data-section="${this.dataset.sectionId}"]`)
  }

  getSwitchInput() {
    const sectionRoot = this.getSectionRoot()
    const switchInput =
      sectionRoot?.querySelector('[data-hero-offer-switch]') || this.querySelector('[data-hero-offer-switch]')
    return switchInput instanceof HTMLInputElement ? switchInput : null
  }

  getSelectedMode() {
    const switchInput = this.getSwitchInput()
    if (switchInput) return switchInput.checked ? 'subscription' : 'onetime'
    return this.dataset.subscriptionActive === 'true' ? 'subscription' : 'onetime'
  }

  getSelectedOffer() {
    const checkedInput = this.querySelector('[data-hero-offer-option-input]:checked')
    const selectedOffer = checkedInput?.closest('[data-hero-offer-option]')
    return selectedOffer || this.querySelector('[data-hero-offer-option]')
  }

  getOfferItems(mode) {
    const offer = this.getSelectedOffer()
    if (!offer) return []

    const isSubscription = mode === 'subscription'
    const items = [1, 2, 3]
      .map((index) => ({
        variantId: offer.getAttribute(`data-product-${index}-variant-id`) || '',
        quantity: '1',
        sellingPlanId: isSubscription ? offer.getAttribute(`data-product-${index}-selling-plan-id`) || '' : '',
      }))
      .filter((item) => item.variantId)

    if (isSubscription && offer.getAttribute('data-bonus-variant-id')) {
      items.push({
        variantId: offer.getAttribute('data-bonus-variant-id'),
        quantity: '1',
        sellingPlanId: '',
      })
    }

    return items
  }

  getBaseInputs(form) {
    if (!form) return []

    const formId = form.getAttribute('id')
    const formScopedInputs = [
      ...form.querySelectorAll("input[name='id'], input[name='quantity'], input[name='selling_plan']"),
    ]
    const externalInputs = formId
      ? [
          ...document.querySelectorAll(
            `input[form='${CSS.escape(formId)}'][name='id'], input[form='${CSS.escape(formId)}'][name='quantity'], input[form='${CSS.escape(formId)}'][name='selling_plan']`
          ),
        ]
      : []

    return [...formScopedInputs, ...externalInputs].filter(
      (input, index, inputs) => input.dataset.heroOfferGenerated !== 'true' && inputs.indexOf(input) === index
    )
  }

  setBaseInputsDisabled(form, disabled) {
    this.getBaseInputs(form).forEach((input) => {
      input.disabled = disabled
    })
  }

  removeOfferInputs(form) {
    form.querySelectorAll("[data-hero-offer-generated='true']").forEach((input) => input.remove())
  }

  appendHiddenInput(form, name, value) {
    if (!value) return

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    input.dataset.heroOfferGenerated = 'true'
    form.append(input)
  }

  syncFormInputs(form, mode) {
    const items = this.getOfferItems(mode)

    this.removeOfferInputs(form)
    this.setBaseInputsDisabled(form, items.length > 0)

    items.forEach((item, index) => {
      this.appendHiddenInput(form, `items[${index}][id]`, item.variantId)
      this.appendHiddenInput(form, `items[${index}][quantity]`, item.quantity)
      this.appendHiddenInput(form, `items[${index}][selling_plan]`, item.sellingPlanId)
    })
  }

  getSelectedOfferValue(name, mode) {
    const offer = this.getSelectedOffer()
    if (!offer) return ''

    const prefix = mode === 'subscription' ? 'subscription' : 'onetime'
    return offer.dataset[`${prefix}${name}`] || ''
  }

  getAtcPrice(mode) {
    const price = this.getSelectedOfferValue('Price', mode)
    if (!price) return ''
    if (mode !== 'subscription') return price
    return price.replace(/\/mo\b/i, '').trim()
  }

  syncAtcCopy(mode) {
    const sectionRoot = this.getSectionRoot()
    if (!sectionRoot) return

    const price = this.getAtcPrice(mode)
    const priceEl = sectionRoot.querySelector('[data-main-product-atc-price]')

    if (priceEl && price) priceEl.textContent = price

    const labelEl = sectionRoot.querySelector('[data-main-product-atc-label]')
    if (!labelEl) return

    const subscriptionLabel = labelEl.dataset.subscriptionLabel || ''
    const onetimeLabel = labelEl.dataset.onetimeLabel || ''
    const fallbackLabel = labelEl.dataset.fallbackLabel || labelEl.textContent.trim()

    if (!subscriptionLabel && !onetimeLabel) return

    const nextLabel = mode === 'subscription' ? subscriptionLabel || fallbackLabel : onetimeLabel || fallbackLabel

    if (nextLabel) labelEl.textContent = nextLabel
  }

  syncOfferSelection() {
    const selectedPosition = this.getSelectedOffer()?.dataset.offerPosition || ''

    this.querySelectorAll('[data-hero-offer-product-line]').forEach((line) => {
      const positions = (line.dataset.offerPositions || '').split(',').filter(Boolean)
      const isExclusive = positions.length === 1
      const isInSelectedOffer = positions.includes(selectedPosition)
      const shouldGray = isExclusive && !isInSelectedOffer

      line.dataset.heroOfferInactive = shouldGray ? 'true' : 'false'
    })
  }

  syncOfferCopy(mode) {
    const isSubscription = mode === 'subscription'
    const subscriptionActive = isSubscription ? 'true' : 'false'

    this.dataset.subscriptionActive = subscriptionActive

    const sectionRoot = this.getSectionRoot()
    if (sectionRoot) {
      sectionRoot.dataset.subscriptionActive = subscriptionActive
    }

    const priceEl = this.querySelector('[data-hero-offer-price]')
    const comparePriceEl = this.querySelector('[data-hero-offer-compare-price]')
    const savingsTextEl = this.querySelector('[data-hero-offer-savings-text]')
    const price = this.getSelectedOfferValue('Price', mode)
    const comparePrice = this.getSelectedOfferValue('ComparePrice', mode)
    const savingsText = this.getSelectedOfferValue('SavingsText', mode)

    if (priceEl) priceEl.textContent = price || ''
    if (comparePriceEl) {
      comparePriceEl.textContent = comparePrice || ''
      comparePriceEl.hidden = !comparePrice
    }
    if (savingsTextEl) {
      savingsTextEl.textContent = savingsText || ''
      savingsTextEl.hidden = !savingsText
    }
  }

  syncMediaCarousel() {
    const sectionRoot = this.getSectionRoot()
    const offerPosition = this.getSelectedOffer()?.dataset.offerPosition || ''
    if (!sectionRoot || !offerPosition) return
    if (offerPosition === this._lastCarouselOfferPosition) return

    this._lastCarouselOfferPosition = offerPosition
    syncHeroOfferCarousels(sectionRoot, offerPosition)
  }

  sync() {
    const form = this.getForm()
    const mode = this.getSelectedMode()

    if (form) this.syncFormInputs(form, mode)
    this.syncOfferSelection()
    this.syncAtcCopy(mode)
    this.syncOfferCopy(mode)
    this.syncMediaCarousel()
  }
}

if (!customElements.get('hero-offer')) {
  customElements.define('hero-offer', HeroOffer)
}

function bootHeroOffer() {
  whenSplideReady(() => {
    initMediaCarousels()
    document.querySelectorAll('hero-offer').forEach((offer) => {
      offer._lastCarouselOfferPosition = null
      if (typeof offer.sync === 'function') offer.sync()
    })
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootHeroOffer)
} else {
  bootHeroOffer()
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target
  if (!target?.matches?.('.section-hero-offer') && !target?.querySelector?.('.section-hero-offer')) return

  whenSplideReady(() => {
    initMediaCarousels(event.target)
    target.querySelectorAll('hero-offer').forEach((offer) => {
      offer._lastCarouselOfferPosition = null
      if (typeof offer.sync === 'function') offer.sync()
    })
  })
})
